import { ChevronDown, X } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { CLOSE_LABEL, PANEL_SUBLINE, PANEL_TITLE } from "./chatCopy";

/**
 * The panel's 64px header: avatar and title, then the close control.
 *
 * THE WHATSAPP ESCAPE HATCH IS GONE, BY PRODUCT DECISION (39-08 checkpoint feedback). It was the
 * only live path while `enabledByChannel.web` was on v1; the web channel now runs v2, so the panel
 * answers for itself. `WHATSAPP_URL` stays in `chatCopy.ts` as the canonical support number.
 *
 * DOM ORDER IS THE CONTRACT: avatar/title, then close. Everything is
 * positioned with logical utilities (`ms-*`, `me-*`, `start-*`, `end-*`) and never with
 * `left-*`/`right-*`, so the reading order and the tab order cannot diverge under RTL.
 *
 * `PANEL_SUBLINE` ("بوت يتفرج") is an honest disclosure, not decoration. It is the same reasoning the
 * deleted WhatsApp button carried in its own docblock: the corner must not read as "start a chat
 * with a human".
 */
export default function ChatHeader({
  isMobile,
  onClose,
  titleId,
}: {
  isMobile: boolean;
  onClose: () => void;
  /** Wired to the desktop dialog's `aria-labelledby`, so the card is named by what it shows. */
  titleId?: string;
}) {
  const CloseIcon = isMobile ? ChevronDown : X;

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-[var(--ys-rule)] bg-[var(--ys-paper)] px-4">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src="/brand/mascot.png" alt="" aria-hidden="true" className="object-contain" />
        <AvatarFallback className="bg-[var(--ys-bg-2)] text-[var(--ys-ink-2)]" aria-hidden="true">
          ن
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div
          id={titleId}
          className="truncate"
          style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: "var(--ys-ink)" }}
        >
          {PANEL_TITLE}
        </div>
        <div
          className="truncate"
          style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: "var(--ys-ink-2)" }}
        >
          {PANEL_SUBLINE}
        </div>
      </div>

      {/* Exactly ONE close affordance in the panel, which is why the mobile sheet renders
          `SheetPrimitive.Content` directly instead of `SheetContent` and its built-in close button.
          `ChevronDown` on mobile because a sheet slides down, so the chevron matches the motion. */}
      <button
        type="button"
        onClick={onClose}
        aria-label={CLOSE_LABEL}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--ys-r-sm)] text-[var(--ys-ink-2)] transition-colors hover:bg-[var(--ys-bg-2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
      >
        <CloseIcon size={20} strokeWidth={2} aria-hidden="true" />
      </button>
    </header>
  );
}
